import {CourierBlockDTO} from "../../types/courier/CourierBlockDTO";
import {DELAYED_ORDER_TEXT, FREE_TEXT, ON_WAY_TEXT, RETURNING_TEXT} from "./Texts";
import {FilterType} from "./Constants";
import {OrderDTO} from "../../types/order/OrderDTO";
import {Courier, CourierDTO} from "../../types/courier/CourierDTO";

import iconProfile from "../../assets/icons/general/ic_profile.svg"
import iconDeliveryPrimary from "../../assets/icons/general/ic_delivery_primary.svg"
import iconDeliveryWarning from "../../assets/icons/general/ic_delivery_warning.svg"

const MILLISECONDS_IN_SECOND = 1000
const courierNameCollator = new Intl.Collator("ru", {numeric: true, sensitivity: "base"})

export const toCourier = (courier: CourierDTO, receivedAtMs: number): Courier => {
    const seconds = courier.secondsSinceLatestDeliveryTime
    return {
        ...courier,
        latestDeliveryTimeMs: typeof seconds === "number" ? receivedAtMs - seconds * MILLISECONDS_IN_SECOND : null
    }
}

const toTimeMs = (dateTime: string | null): number | null => {
    if (!dateTime) {
        return null
    }
    const timeMs = Date.parse(dateTime)
    return Number.isNaN(timeMs) ? null : timeMs
}

const toShownName = (courier: CourierDTO): string => courier.displayName ?? courier.firstName

/** Ascending, with unknown values last. */
const compareKnownFirst = (first: number | null, second: number | null): number => {
    if (first === null || second === null) {
        return Number(first === null) - Number(second === null)
    }
    return first - second
}

/** Breaks ties, so the order never depends on the order couriers arrived in. */
const compareIikoIds = (first: CourierDTO, second: CourierDTO): number =>
    first.iikoId.localeCompare(second.iikoId)

const compareByFreeTime = (first: CourierDTO, second: CourierDTO): number =>
    compareKnownFirst(toTimeMs(first.freeStatusDateTime), toTimeMs(second.freeStatusDateTime))
    || compareIikoIds(first, second)

const compareByShownName = (first: CourierDTO, second: CourierDTO): number =>
    courierNameCollator.compare(toShownName(first), toShownName(second))
    || compareIikoIds(first, second)

const compareByLatestDelivery = (first: Courier, second: Courier): number =>
    compareKnownFirst(first.latestDeliveryTimeMs, second.latestDeliveryTimeMs)
    || compareIikoIds(first, second)

const compareByIds = (first: CourierDTO, second: CourierDTO): number => first.id - second.id

export const COURIER_BLOCK_FREE_ID=1
export const COURIER_BLOCK_ON_WAY_ID=2
export const COURIER_BLOCK_RETURNING_ID=3
export const COURIER_BLOCK_DELAYED_ID=4
export const COURIER_BLOCKS = (hasFilter: (item: FilterType) => boolean, orders?: OrderDTO[] | null): CourierBlockDTO[] => ([
    {
        id: COURIER_BLOCK_FREE_ID,
        status: "free",
        title: FREE_TEXT,
        icon: iconProfile,
        titleType: "visibilitySwitcher",
        visibleOnMap: hasFilter("free"),
        color: "success",
        showCloseButton: true,
        showBody: true,
        filter: (item) => item.status === "free",
        sort: compareByFreeTime
    },
    {
        id: COURIER_BLOCK_ON_WAY_ID,
        status: "onWay",
        title: ON_WAY_TEXT,
        icon: iconDeliveryPrimary,
        titleType: "visibilitySwitcher",
        visibleOnMap: hasFilter("onWay"),
        color: "primary",
        showCloseButton: false,
        showBody: true,
        filter: (item) => item.status === "onWay",
        sort: compareByShownName
    },
    {
        id: COURIER_BLOCK_RETURNING_ID,
        status: "returning",
        title: RETURNING_TEXT,
        icon:iconDeliveryWarning,
        titleType: "visibilitySwitcher",
        visibleOnMap: hasFilter("returning"),
        color: "warning",
        showCloseButton: false,
        showBody: true,
        filter: (item) => item.status === "returning",
        sort: compareByLatestDelivery
    },
    {
        id: COURIER_BLOCK_DELAYED_ID,
        title: DELAYED_ORDER_TEXT,
        titleType: "orderCount",
        visibleOnMap: false,
        color: "warning",
        showCloseButton: false,
        showBody: false,
        filter: (item) => !(!orders?.filter(order=> order.courierId===item.iikoId && order.status==="Delayed")?.length),
        sort: compareByIds
    }
])

// COURIER STATUS TYPE
export type CourierStatus = 'onWay'
    | 'free'
    | 'returning'
    | 'arrived'
    | 'notWorking'
    | "reserved"

